import React, { useLayoutEffect, useMemo, useState } from 'react'
import { toSVG, VB_W, VB_H } from './geo'

/** Hourly meridians: -180, -165, … 165 (24 lines). */
export const MERIDIAN_LONS: number[] = Array.from({ length: 24 }, (_, i) => -180 + i * 15)

/** UTC offset (hours) for a longitude — nominal 15°/hour. */
export function lonToUtcOffset(lon: number): number {
  let L = lon
  while (L > 180) L -= 360
  while (L < -180) L += 360
  return Math.round(L / 15)
}

/** Longitude for a map-space x (inverse of toSVG x). */
export function mapXToLon(x: number): number {
  return (x / VB_W) * 360 - 180
}

function fmtOffset(off: number): string {
  if (off === 0) return 'UTC'
  return off > 0 ? `+${off}` : `${off}`
}

/** Current wall-clock time at a UTC offset, as HH:MM. */
export function localTimeForOffset(off: number, now: Date = new Date()): string {
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60_000
  const d = new Date(utcMs + off * 3_600_000)
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${hh}:${mm}`
}

/**
 * Faint hourly meridian lines inside the pan/zoom group. Stroke width is divided
 * by zoom so lines stay hairline.
 */
export function MeridianLines({ zoom }: { zoom: number }) {
  const w = 0.6 / Math.max(zoom, 0.001)
  return (
    <g pointerEvents="none">
      {MERIDIAN_LONS.map((lon) => {
        const x = toSVG(0, lon).x
        const isPrime = lon === 0
        return (
          <line
            key={lon}
            x1={x} y1={0} x2={x} y2={VB_H}
            stroke={isPrime ? 'rgba(150,180,255,0.22)' : 'rgba(150,180,255,0.10)'}
            strokeWidth={w}
          />
        )
      })}
    </g>
  )
}

function viewBoxToContainerPct(
  svg: SVGSVGElement,
  viewBoxX: number,
  viewBoxY: number,
): number | null {
  const container = svg.parentElement
  if (!container) return null
  const pt = svg.createSVGPoint()
  pt.x = viewBoxX
  pt.y = viewBoxY
  const ctm = svg.getScreenCTM()
  if (!ctm) return null
  const screen = pt.matrixTransform(ctm)
  const rect = container.getBoundingClientRect()
  if (!rect.width) return null
  return ((screen.x - rect.left) / rect.width) * 100
}

/**
 * Bottom UTC-offset axis pinned to the map container edges (HTML overlay).
 * Tick x-positions follow the panned map via mapToScreen + SVG CTM.
 */
export function TimezoneAxisOverlay({
  svgRef,
  mapToScreen,
  zoom,
  pan,
}: {
  svgRef: React.RefObject<SVGSVGElement | null>
  mapToScreen: (x: number, y: number) => { x: number; y: number }
  zoom: number
  pan: { x: number; y: number }
}) {
  const [, bump] = useState(0)

  useLayoutEffect(() => {
    const el = svgRef.current
    const container = el?.parentElement
    if (!el || !container) return
    const ro = new ResizeObserver(() => bump((n) => n + 1))
    ro.observe(container)
    return () => ro.disconnect()
  }, [svgRef])

  const ticks = useMemo(() => {
    const svg = svgRef.current
    if (!svg) return []
    return MERIDIAN_LONS.map((lon) => {
      const mapX = toSVG(0, lon).x
      const screen = mapToScreen(mapX, 0)
      const pct = viewBoxToContainerPct(svg, screen.x, screen.y)
      if (pct == null) return null
      const major = ((lon % 45) + 360) % 45 === 0
      return { lon, pct, off: lonToUtcOffset(lon), major }
    }).filter((t): t is { lon: number; pct: number; off: number; major: boolean } =>
      t != null && t.pct >= -2 && t.pct <= 102,
    )
  }, [svgRef, mapToScreen, zoom, pan.x, pan.y, bump])

  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 34,
        pointerEvents: 'none',
        zIndex: 3,
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          height: 1,
          background: 'rgba(150,180,255,0.22)',
        }}
      />
      {ticks.map((t) => (
        <React.Fragment key={t.lon}>
          <div
            style={{
              position: 'absolute',
              left: `${t.pct}%`,
              top: 0,
              width: 1,
              height: t.major ? 6 : 3,
              marginLeft: -0.5,
              background: 'rgba(150,180,255,0.32)',
            }}
          />
          {t.major && (
            <div
              style={{
                position: 'absolute',
                left: `${t.pct}%`,
                top: 10,
                transform: 'translateX(-50%)',
                fontSize: 10,
                color: 'var(--t-2, #7070a0)',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
              }}
            >
              {fmtOffset(t.off)}
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  )
}
